import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api, { API, setToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Activity, ShieldCheck, GitBranch, User, Users, Building2, Eye, EyeOff, Check } from "lucide-react";
import cubeLogo from "../assets/tm-cube.png";

const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

const TYPE_ICONS = { individual: User, team: Users, organisation: Building2 };

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [brand, setBrand] = useState(null);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [f, setF] = useState({
    full_name: "", email: "", mobile: "", account_type: "",
    organization_name: "", team_name: "", team_size: "", industry: "",
    password: "", confirm_password: "", accept_terms: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${API}/branding/public`).then((r) => (r.ok ? r.json() : null)).then(setBrand).catch(() => {});
    api.get("/auth/signup-config").then(({ data }) => setConfig(data)).catch(() => setConfig({ public_signup_enabled: false, account_types: [] }));
  }, []);

  const productName = brand?.product_name || "Task Matrix";
  const tagline = brand?.tagline || "Tasks. Ownership. Execution.";
  const bg = brand?.login_background || "#0B1120";
  const logoUrl = absUrl(brand?.logo_url) || cubeLogo;

  const next = () => {
    if (step === 1) {
      if (!f.full_name.trim()) return toast.error("Please enter your name");
      if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Please enter a valid email");
      if (config?.mobile_verification_required && !f.mobile.trim()) return toast.error("Mobile number is required");
    }
    if (step === 2 && !f.account_type) return toast.error("Please choose an account type");
    if (step === 3) {
      if (f.account_type === "team" && !f.team_name.trim()) return toast.error("Please enter a team name");
      if (f.account_type === "organisation" && !f.organization_name.trim()) return toast.error("Please enter your organisation name");
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const submit = async () => {
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (f.password !== f.confirm_password) return toast.error("Passwords do not match");
    if (!f.accept_terms) return toast.error("Please accept the Terms & Privacy Policy");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", f);
      if (data.verification_required) {
        toast.success("Account created — check your email to verify");
        nav("/login");
        return;
      }
      if (data.access_token) setToken(data.access_token);
      setUser(data.user);
      toast.success(config?.welcome_message || "Welcome to Task Matrix!");
      nav(data.account_type === "individual" ? "/dashboard" : "/workspaces");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Activity, title: "Clarity", desc: "One source of truth for every task" },
    { icon: ShieldCheck, title: "Accountability", desc: "Clear owners on every commitment" },
    { icon: GitBranch, title: "Visibility", desc: "Dependencies & status at a glance" },
  ];

  const BrandPanel = (
    <div className="hidden lg:flex flex-col justify-between w-[52%] text-white px-16 py-12" style={{ background: bg }}>
      <div className="flex items-center gap-4">
        <img src={logoUrl} alt={productName} className="h-14 w-auto" />
        <div className="border-l border-slate-700 pl-4">
          <div className="text-base font-semibold tracking-wide">{productName}</div>
          <div className="text-xs tracking-wide text-cyan-400">{tagline}</div>
        </div>
      </div>
      <div className="max-w-lg">
        <h1 className="text-4xl font-bold tracking-tight leading-tight">Create your workspace</h1>
        <p className="mt-6 text-slate-400 text-lg leading-relaxed">Set up your account in under a minute. Bring your tasks, ownership and execution into one system.</p>
        <div className="mt-12 grid grid-cols-3 gap-4">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
              <Icon size={20} className="text-cyan-400 mb-3" />
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-xs text-slate-600 tracking-wide">✦&nbsp;&nbsp;Powered by Mind Compute&nbsp;&nbsp;|&nbsp;&nbsp;© 2026 {productName}</div>
    </div>
  );

  if (config && !config.public_signup_enabled) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {BrandPanel}
        <div className="flex-1 flex items-center justify-center bg-white px-6">
          <div className="text-center max-w-sm" data-testid="signup-disabled">
            <h2 className="text-2xl font-bold text-slate-900">Sign-up is closed</h2>
            <p className="text-slate-500 mt-2 text-sm">Public registration is currently disabled. Please contact your administrator for an invitation.</p>
            <Link to="/login" className="inline-block mt-6 text-blue-600 hover:underline text-sm">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  const steps = ["Your details", "Account type", "About you", "Password"];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {BrandPanel}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md" data-testid="signup-form">
            <div className="lg:hidden flex flex-col items-center mb-6">
              <img src={logoUrl} alt={productName} className="h-14 w-auto mb-2" />
            </div>
            {/* stepper */}
            <div className="flex items-center gap-2 mb-8">
              {steps.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {step > i + 1 ? <Check size={14} /> : i + 1}
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${step > i + 1 ? "bg-emerald-500" : "bg-slate-100"}`} />}
                </React.Fragment>
              ))}
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">{steps[step - 1]}</h2>
            <p className="text-slate-500 text-sm mt-1 mb-6">Step {step} of 4</p>

            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Full name</Label>
                  <Input data-testid="signup-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Cooper" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input data-testid="signup-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label>Mobile {config?.mobile_verification_required ? "" : "(optional)"}</Label>
                  <Input data-testid="signup-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98765 43210" className="h-11" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                {(config?.account_types || []).map((t) => {
                  const Icon = TYPE_ICONS[t.code] || User;
                  const active = f.account_type === t.code;
                  return (
                    <button key={t.code} type="button" data-testid={`signup-type-${t.code}`} onClick={() => set("account_type", t.code)}
                      className={`w-full flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${active ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}><Icon size={18} /></div>
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{t.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>
                      </div>
                    </button>
                  );
                })}
                {(config?.account_types || []).length === 0 && <p className="text-sm text-slate-400">No account types are available right now.</p>}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {f.account_type === "individual" && (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600" data-testid="signup-individual-note">
                    You're all set — individual accounts don't need any extra details. Continue to set your password.
                  </div>
                )}
                {f.account_type === "team" && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Team name</Label>
                      <Input data-testid="signup-team-name" value={f.team_name} onChange={(e) => set("team_name", e.target.value)} placeholder="Growth Team" className="h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Team size (optional)</Label>
                      <Input data-testid="signup-team-size" value={f.team_size} onChange={(e) => set("team_size", e.target.value)} placeholder="e.g. 5-10" className="h-11" />
                    </div>
                  </>
                )}
                {f.account_type === "organisation" && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Organisation name</Label>
                      <Input data-testid="signup-org-name" value={f.organization_name} onChange={(e) => set("organization_name", e.target.value)} placeholder="Acme Corporation" className="h-11" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Industry (optional)</Label>
                      <Input data-testid="signup-industry" value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Real Estate" className="h-11" />
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <div className="relative">
                    <Input data-testid="signup-password" type={showPw ? "text" : "password"} value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" className="h-11 pr-10" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Confirm password</Label>
                  <Input data-testid="signup-confirm" type={showPw ? "text" : "password"} value={f.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} placeholder="Re-enter password" className="h-11" />
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" data-testid="signup-terms" checked={f.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} className="mt-0.5" />
                  <span>
                    I agree to the{" "}
                    {config?.terms_url ? <a href={config.terms_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Terms</a> : "Terms"} and{" "}
                    {config?.privacy_url ? <a href={config.privacy_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a> : "Privacy Policy"}.
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-center gap-3 mt-8">
              {step > 1 && <Button type="button" variant="outline" data-testid="signup-back" onClick={() => setStep((s) => s - 1)} className="h-11">Back</Button>}
              {step < 4 ? (
                <Button type="button" data-testid="signup-next" onClick={next} className="h-11 flex-1">Continue</Button>
              ) : (
                <Button type="button" data-testid="signup-submit" onClick={submit} disabled={loading} className="h-11 flex-1">{loading ? "Creating…" : "Create account"}</Button>
              )}
            </div>
            <p className="text-center text-sm text-slate-500 mt-6">
              Already have an account? <Link to="/login" data-testid="signup-to-login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
